<script setup lang="ts">
import type { Property } from '~/core/entities/property';

import { EventBusKeys } from '~/core/helpers/symbols';
import { cn } from '~/utils';
import { ref } from 'vue';

import IconMaterialSymbolsLightChatBubbleOutlineSharp from '~icons/material-symbols-light/chat-bubble-outline-sharp';
import IconMaterialSymbolsInHomeModeSharp from '~icons/material-symbols/in-home-mode-sharp';

import { Button } from '../ui/button';
import SimilarPropertiesModal from './SimilarPropertiesModal.vue';

interface PropertyCardActionsProps {
	showActions?: boolean;
	property: Property;
	isPropertyActive?: boolean;
}

const props = withDefaults(defineProps<PropertyCardActionsProps>(), {
	showActions: true,
	isPropertyActive: true
});

const isSimilarModalOpen = ref(false);
const isContactModalOpen = ref(false);

const contactBrokerClickBus = useEventBus(EventBusKeys.contactBrokerClick);
const similarPropertiesClickBus = useEventBus(
	EventBusKeys.similarPropertiesClick
);

function handleSimilarPropertiesClick(): void {
	isSimilarModalOpen.value = true;
	similarPropertiesClickBus.emit({
		origin: 'card',
		commercialId: props.property.commercialId
	});
}

function handleContactBrokerClick(): void {
	isContactModalOpen.value = true;
	contactBrokerClickBus.emit({
		commercialId: props.property.commercialId,
		askingPrice: props.property.askingPrice ?? 0,
		companyName: props.property.company?.name ?? '',
		agentName: props.property.agent?.name ?? ''
	});
}
</script>
<template>
	<div
		v-if="showActions"
		:class="cn('flex w-full gap-2', $attrs.class as string)"
	>
		<Button
			variant="secondary"
			size="xs"
			skeleton-classes="w-full flex-1"
			class="gap-2"
			:is-client-only="false"
			data-testid="similar-properties-button"
			@click.prevent.stop="handleSimilarPropertiesClick"
		>
			<IconMaterialSymbolsInHomeModeSharp class="size-4" />
			Semelhantes
		</Button>

		<Button
			v-if="isPropertyActive"
			variant="secondary"
			size="xs"
			class="gap-2"
			skeleton-classes="w-full flex-1"
			:is-client-only="false"
			data-testid="contact-button"
			@click.prevent.stop="handleContactBrokerClick"
		>
			<IconMaterialSymbolsLightChatBubbleOutlineSharp class="size-4" />
			Contactar
		</Button>
	</div>

	<ClientOnly>
		<SimilarPropertiesModal
			v-model:is-open="isSimilarModalOpen"
			:base-property="property"
		/>
	</ClientOnly>

	<ClientOnly>
		<LazyContactFormModal
			v-model:model-value="isContactModalOpen"
			:property-id="property.id"
			:property-commercial-id="property.commercialId"
			:broker="property.agent"
			:company-name="property.company?.name"
			:company-logo="property.company?.smallLogo"
			context="property"
			:default-expanded="false"
		/>
	</ClientOnly>
</template>
